
/**
 * Hashes a string to a stable integer in the range [0, 99999].
 * Uses a simple djb2-style multiply-and-XOR hash.
 */
export function hashStringToSeed(str: string): number {
  let h = 2166136261; // FNV-1a offset basis (32-bit)
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (Math.imul(h, 16777619) >>> 0); // FNV prime, keep unsigned 32-bit
  }
  return h % 100000;
}

// ── Surface caches ────────────────────────────────────────────────────────
// Surfaces are deterministic for a given (seed, options) pair, so generated
// results are safe to reuse for the lifetime of the page. This avoids
// regenerating expensive SVG data-URL strings on every re-render, which is
// particularly impactful for list components (WarbandMemberCard, SpellCard)
// that call surface factories per item without memoization.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _iceSurfaceCache = new Map<string, any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _subtleIceSurfaceCache = new Map<string, any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _parchmentSurfaceCache = new Map<number, any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _castIronSurfaceCache = new Map<number, any>();
const _iceEdgeMaskCache = new Map<number, Record<string, string>>();
const _raggedEdgeMaskCache = new Map<number, Record<string, string>>();
const _handDrawnBorderCache = new Map<number, Record<string, string>>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _frostedGlassSurfaceCache = new Map<number, any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _parchmentLabelSurfaceCache = new Map<number, any>();

/**
 * Generates a procedural SVG ice texture as a CSS data: URL.
 *
 * Two feTurbulence layers are blended together:
 *  - fractalNoise: smooth large-scale crystal structure
 *  - turbulence:   sharp high-frequency surface cracks and imperfections
 * A feColorMatrix maps the greyscale blend to an icy blue-white palette.
 *
 * @param seed - Controls the unique crystal arrangement (0–99999)
 */
function generateIceSVG(seed: number): string {
  const crackSeed = (seed * 7 + 13) % 99999;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">` +
    `<filter id="f" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">` +
    // Primary crystal structure — smooth fractal noise at large scale
    `<feTurbulence type="fractalNoise" baseFrequency="0.025 0.060" numOctaves="6" seed="${seed}" result="crys"/>` +
    // Surface cracks / imperfections — sharp turbulence at higher frequency
    `<feTurbulence type="turbulence" baseFrequency="0.095 0.160" numOctaves="2" seed="${crackSeed}" result="crack"/>` +
    // Multiply blend: cracks darken where they overlay the crystal base
    `<feBlend in="crys" in2="crack" mode="multiply" result="blend"/>` +
    // Map greyscale noise to icy blue-white palette (semi-transparent)
    // Matrix rows: R G B A offset — maps noise → (ice-blue cracks, white highlights)
    `<feColorMatrix type="matrix" ` +
    `values="0.55 0 0 0 0.42  0.20 0.35 0 0 0.62  0 0 0.55 0 0.78  0 0 0 0.30 0" ` +
    `in="blend"/>` +
    `</filter>` +
    `<rect width="512" height="512" filter="url(#f)"/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Generates CSS mask properties that produce chipped, cracked ice edges
 * with a subtle melt-fade into the background.
 *
 * Uses the same multi-strip architecture as `generateRaggedEdgeMask` (solid
 * centre + four edge strips + four corner pieces at fixed CSS-pixel size)
 * but applies a two-stage displacement for ice-specific character:
 *
 *  1. Low-frequency turbulence → large chip-outs where whole chunks are
 *     missing (the way real ice snaps in broad, conchoidal fractures).
 *  2. High-frequency turbulence → fine crystalline jaggedness layered on
 *     top (sharp micro-edges along the fracture surface).
 *
 * After displacement a light Gaussian blur + component-transfer ramp
 * softens the outermost fringe of the mask to create a translucent
 * "melt zone" — the ice appears to thin and fade before it ends, rather
 * than cutting off sharply like the parchment variant.
 *
 * @param seed - Controls the unique edge fracture pattern (0–99999)
 * @returns CSS mask properties to spread into a style object
 */
function generateIceEdgeMask(seed: number): Record<string, string> {
  const _hit = _iceEdgeMaskCache.get(seed);
  if (_hit !== undefined) return _hit;
  // Wide edge zone to give the melt gradient room to breathe
  const EDGE_PX = 28;
  // SVG units: thin axis (edge depth) and long axis (along-edge)
  const THIN = 60;
  const LONG = 2000;
  // Two-scale displacement: large chip-outs then crystalline teeth
  const CHIP_DISP = 18;
  const JAG_DISP = 10;
  // Where the rect's ragged edge sits in the thin axis (SVG units)
  const INSET = 16;
  // Rect overshoot past SVG bounds on the three non-ragged sides
  const PAD = 28;

  const mkSvg = (
    w: number, h: number,
    rx: number, ry: number, rw: number, rh: number,
    edgeSeed: number,
    chipBfx: number, chipBfy: number,
    jagBfx: number, jagBfy: number,
  ) => {
    const jagSeed = (edgeSeed * 3 + 41) % 99999;
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" preserveAspectRatio="none">` +
      `<filter id="f">` +
      // Stage 1 — broad conchoidal chip-outs
      `<feTurbulence type="turbulence" baseFrequency="${chipBfx} ${chipBfy}" numOctaves="3" seed="${edgeSeed}" result="chips"/>` +
      `<feDisplacementMap in="SourceGraphic" in2="chips" scale="${CHIP_DISP}" xChannelSelector="R" yChannelSelector="G" result="chipped"/>` +
      // Stage 2 — fine crystalline jaggedness on top of the chip-outs
      `<feTurbulence type="turbulence" baseFrequency="${jagBfx} ${jagBfy}" numOctaves="5" seed="${jagSeed}" result="jag"/>` +
      `<feDisplacementMap in="chipped" in2="jag" scale="${JAG_DISP}" xChannelSelector="R" yChannelSelector="G" result="cracked"/>` +
      // Melt fade — moderate blur preserves chip definition while creating
      // a translucent fringe; steeper alpha ramp keeps chips crisp inside
      `<feGaussianBlur in="cracked" stdDeviation="2.0" result="melted"/>` +
      `<feComponentTransfer in="melted">` +
      `<feFuncA type="linear" slope="2.4" intercept="-0.12"/>` +
      `</feComponentTransfer>` +
      `</filter>` +
      `<rect x="${rx}" y="${ry}" width="${rw}" height="${rh}" fill="white" filter="url(#f)"/>` +
      `</svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  };

  // ── Turbulence frequencies ──────────────────────────────────────────────
  // Chip layer: low along-edge freq ⇒ broad chunks missing
  // Jag layer:  high along-edge freq ⇒ dense crystalline teeth
  // Horizontal edges (along-edge = X, thin-axis = Y)
  const hChipX = 0.006, hChipY = 0.04;
  const hJagX = 0.015, hJagY = 0.10;
  // Vertical edges (swapped)
  const vChipX = 0.04, vChipY = 0.006;
  const vJagX = 0.10, vJagY = 0.015;
  // Corners: moderate in both axes for coherent angular breaks
  const cChip = 0.03, cJag = 0.07;

  // --- Edge strips (exclude corners — sized to inner area only) ----------
  // Top: ragged on the bottom edge of the strip (rect overshoots top/left/right)
  const topUrl = mkSvg(LONG, THIN,
    -PAD, INSET, LONG + PAD * 2, THIN - INSET + PAD,
    seed, hChipX, hChipY, hJagX, hJagY);
  // Bottom: ragged on the top edge of the strip
  const bottomUrl = mkSvg(LONG, THIN,
    -PAD, -PAD, LONG + PAD * 2, THIN - INSET + PAD,
    (seed * 3 + 7) % 99999, hChipX, hChipY, hJagX, hJagY);
  // Left: ragged on the right edge of the strip
  const leftUrl = mkSvg(THIN, LONG,
    INSET, -PAD, THIN - INSET + PAD, LONG + PAD * 2,
    (seed * 5 + 11) % 99999, vChipX, vChipY, vJagX, vJagY);
  // Right: ragged on the left edge of the strip
  const rightUrl = mkSvg(THIN, LONG,
    -PAD, -PAD, THIN - INSET + PAD, LONG + PAD * 2,
    (seed * 7 + 13) % 99999, vChipX, vChipY, vJagX, vJagY);

  // --- Corner pieces — coherent fracture across both exposed edges -------
  const tlUrl = mkSvg(THIN, THIN,
    INSET, INSET, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 11 + 17) % 99999, cChip, cChip, cJag, cJag);
  const trUrl = mkSvg(THIN, THIN,
    -PAD, INSET, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 13 + 19) % 99999, cChip, cChip, cJag, cJag);
  const brUrl = mkSvg(THIN, THIN,
    -PAD, -PAD, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 17 + 23) % 99999, cChip, cChip, cJag, cJag);
  const blUrl = mkSvg(THIN, THIN,
    INSET, -PAD, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 19 + 29) % 99999, cChip, cChip, cJag, cJag);

  const E = `${EDGE_PX}px`;
  const inner = `calc(100% - ${EDGE_PX * 2}px)`;

  const images = [
    // Solid centre fill
    'linear-gradient(white, white)',
    // Edge strips (sans corners)
    topUrl, rightUrl, bottomUrl, leftUrl,
    // Corner pieces
    tlUrl, trUrl, brUrl, blUrl,
  ].join(', ');

  const sizes = [
    `${inner} ${inner}`,   // centre
    `${inner} ${E}`,       // top
    `${E} ${inner}`,       // right
    `${inner} ${E}`,       // bottom
    `${E} ${inner}`,       // left
    `${E} ${E}`,           // top-left
    `${E} ${E}`,           // top-right
    `${E} ${E}`,           // bottom-right
    `${E} ${E}`,           // bottom-left
  ].join(', ');

  const positions = [
    'center',
    'center top', 'right center', 'center bottom', 'left center',
    'left top', 'right top', 'right bottom', 'left bottom',
  ].join(', ');

  const _r: Record<string, string> = {
    WebkitMaskImage: images,
    maskImage: images,
    WebkitMaskSize: sizes,
    maskSize: sizes,
    WebkitMaskPosition: positions,
    maskPosition: positions,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
  };
  _iceEdgeMaskCache.set(seed, _r);
  return _r;
}

/**
 * Generates a procedural SVG row of small icicles as a CSS data: URL.
 *
 * Uses a seeded PRNG (mulberry32) to place 18–28 tapered icicles of
 * varying height (6–30 px) across a 400 px tile. Each icicle is an
 * organic cubic-bezier shape — wider at the top where it clings to the
 * edge, tapering to a sharp drip point. Some icicles have a small melt
 * droplet near the tip.
 *
 * The SVG tiles seamlessly on the X axis via `background-repeat: repeat-x`.
 *
 * @param seed - Controls icicle placement and variety (0–99999)
 */
function generateIciclesSVG(seed: number): string {
  // Mulberry32 seeded PRNG
  const prng = (s: number) => {
    let t = s + 0x6d2b79f5;
    return () => {
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };
  const rand = prng(seed);

  const W = 400;
  const H = 36;

  // Icicle fill: gradient matched to the ice surface body colour
  // (rgb(200,225,248) @ 0.68) — opaque at the root fading toward the tip
  let svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">` +
    `<defs>` +
    `<linearGradient id="ig" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0%" stop-color="rgba(200,225,248,0.52)"/>` +
    `<stop offset="35%" stop-color="rgba(200,225,248,0.38)"/>` +
    `<stop offset="100%" stop-color="rgba(200,225,248,0.15)"/>` +
    `</linearGradient>` +
    // Highlight gradient — glassy specular on the centre
    `<linearGradient id="ih" x1="0.3" y1="0" x2="0.7" y2="1">` +
    `<stop offset="0%" stop-color="rgba(240,250,255,0.35)"/>` +
    `<stop offset="50%" stop-color="rgba(230,245,255,0.10)"/>` +
    `<stop offset="100%" stop-color="rgba(230,245,255,0)"/>` +
    `</linearGradient>` +
    // Soft glow filter — tinted to match ice surface hue
    `<filter id="if" x="-10%" y="-5%" width="120%" height="115%">` +
    `<feGaussianBlur in="SourceGraphic" stdDeviation="0.4" result="soft"/>` +
    `<feColorMatrix in="soft" type="matrix" ` +
    `values="1 0 0 0 0.04  0 1 0 0 0.06  0 0 1 0 0.10  0 0 0 1.1 0" result="lit"/>` +
    `<feMerge><feMergeNode in="lit"/><feMergeNode in="SourceGraphic"/></feMerge>` +
    `</filter>` +
    `</defs>`;

  // Place icicles left to right across the tile
  const count = 18 + Math.floor(rand() * 11);
  const baseSpacing = W / count;
  let x = rand() * baseSpacing * 0.5; // start with slight offset

  for (let i = 0; i < count; i++) {
    const h = 6 + rand() * 24;            // height 6–30
    const w = 3 + rand() * 4.5;           // width at root 3–7.5
    const hw = w / 2;
    // Subtle horizontal jitter
    const cx = x + (rand() - 0.5) * baseSpacing * 0.3;
    // Organic cubic-bezier control points for left and right curves
    const lCpY1 = h * (0.25 + rand() * 0.15);
    const lCpY2 = h * (0.70 + rand() * 0.18);
    const rCpY2 = h * (0.70 + rand() * 0.18);
    const rCpY1 = h * (0.25 + rand() * 0.15);
    // Slight inward lean — control points don't sit exactly at hw
    const lCpX1 = hw * (0.85 + rand() * 0.3);
    const lCpX2 = hw * (0.12 + rand() * 0.20);
    const rCpX2 = hw * (0.12 + rand() * 0.20);
    const rCpX1 = hw * (0.85 + rand() * 0.3);

    const path =
      `M${(cx - hw).toFixed(1)},0 ` +
      `C${(cx - lCpX1).toFixed(1)},${lCpY1.toFixed(1)} ` +
      `${(cx - lCpX2).toFixed(1)},${lCpY2.toFixed(1)} ` +
      `${cx.toFixed(1)},${h.toFixed(1)} ` +
      `C${(cx + rCpX2).toFixed(1)},${rCpY2.toFixed(1)} ` +
      `${(cx + rCpX1).toFixed(1)},${rCpY1.toFixed(1)} ` +
      `${(cx + hw).toFixed(1)},0 Z`;

    // Main icicle shape
    svg += `<path d="${path}" fill="url(#ig)" filter="url(#if)"/>`;
    // Specular highlight stripe (thinner, centred)
    const hlHw = hw * 0.35;
    const hlPath =
      `M${(cx - hlHw).toFixed(1)},0 ` +
      `C${(cx - hlHw * 0.7).toFixed(1)},${(lCpY1 * 0.9).toFixed(1)} ` +
      `${(cx - hlHw * 0.15).toFixed(1)},${(lCpY2 * 0.85).toFixed(1)} ` +
      `${cx.toFixed(1)},${(h * 0.92).toFixed(1)} ` +
      `C${(cx + hlHw * 0.15).toFixed(1)},${(rCpY2 * 0.85).toFixed(1)} ` +
      `${(cx + hlHw * 0.7).toFixed(1)},${(rCpY1 * 0.9).toFixed(1)} ` +
      `${(cx + hlHw).toFixed(1)},0 Z`;
    svg += `<path d="${hlPath}" fill="url(#ih)" opacity="0.6"/>`;

    // ~30 % chance of a tiny melt droplet near the tip
    if (rand() < 0.30) {
      const dr = 1.0 + rand() * 1.2;
      const dy = h + dr * 1.2 + rand() * 2;
      svg += `<circle cx="${cx.toFixed(1)}" cy="${dy.toFixed(1)}" r="${dr.toFixed(1)}" ` +
        `fill="rgba(200,225,248,0.40)"/>`;
    }

    x += baseSpacing * (0.7 + rand() * 0.6);
  }

  svg += `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export function createSubtleIceSurface(
  seed: number,
  options?: { transparent?: boolean; rainbowSheen?: boolean },
) {
  const t = options?.transparent ?? false;
  const rainbow = options?.rainbowSheen ?? false;
  const _cacheKey = `${seed}:${t ? 1 : 0}:${rainbow ? 1 : 0}`;
  const _hit = _subtleIceSurfaceCache.get(_cacheKey);
  if (_hit !== undefined) return _hit;

  // Derive a stable angle from the seed so each instance refracts differently
  const rainbowAngle = 110 + (seed % 80); // 110°–190° range

  // ── Rainbow iridescence layer ───────────────────────────────────────────
  // A diagonal multi-stop gradient simulating prismatic light refraction
  // through the ice. Layered on top of the normal ice texture at low
  // opacity so the crystal structure still shows through.
  const rainbowGradient = rainbow
    ? `linear-gradient(${rainbowAngle}deg, ` +
    `rgba(255,80,80,${t ? 0.08 : 0.14}) 0%, ` +
    `rgba(255,170,60,${t ? 0.07 : 0.12}) 14%, ` +
    `rgba(255,240,80,${t ? 0.07 : 0.12}) 28%, ` +
    `rgba(80,220,120,${t ? 0.07 : 0.12}) 42%, ` +
    `rgba(80,180,255,${t ? 0.08 : 0.14}) 57%, ` +
    `rgba(130,100,255,${t ? 0.07 : 0.12}) 71%, ` +
    `rgba(200,80,220,${t ? 0.07 : 0.12}) 85%, ` +
    `rgba(255,80,80,${t ? 0.06 : 0.10}) 100%)`
    : null;

  // A secondary radial iridescent hotspot for extra depth
  const rainbowRadial = rainbow
    ? `radial-gradient(ellipse 70% 50% at 65% 70%, ` +
    `rgba(160,120,255,${t ? 0.06 : 0.10}) 0%, ` +
    `rgba(80,200,220,${t ? 0.04 : 0.07}) 40%, ` +
    `rgba(255,255,255,0) 100%)`
    : null;

  // ── Shared surface properties ───────────────────────────────────────────
  const bgImage = [
    // Rainbow layers (if enabled) sit on top of the ice stack
    ...(rainbowGradient ? [rainbowGradient] : []),
    ...(rainbowRadial ? [rainbowRadial] : []),
  ];
  const rainbowExtraSizes = [
    ...(rainbowGradient ? ['cover'] : []),
    ...(rainbowRadial ? ['cover'] : []),
  ];
  const bgSize = [...rainbowExtraSizes, 'cover', 'cover', '512px 512px'];
  const borderVal = rainbow
    ? (t
      ? '1px solid rgba(200, 180, 255, 0.45)'
      : '1px solid rgba(220, 200, 255, 0.88)')
    : (t ? '1px solid rgba(230, 245, 255, 0.40)' : '1px solid rgba(230, 245, 255, 0.92)');

  const _result = {
    backgroundColor: '#02061714',
    backgroundImage: bgImage.join(', '),
    backgroundSize: bgSize.join(', '),
    border: borderVal,
    boxShadow:
      'inset 0 3px 8px rgba(5, 20, 45, 0.28), ' +
      'inset 0 1px 3px rgba(5, 20, 45, 0.18), ' +
      'inset 0 -1px 0 rgba(240, 250, 255, 0.12)',
    // Chipped, cracked ice edges with melt fade (size-independent)
    ...generateIceEdgeMask(seed),
  };
  _subtleIceSurfaceCache.set(_cacheKey, _result);
  return _result;
}

// ── Glass colour palette ─────────────────────────────────────────────────
// Eight liquid tints indexed by seed % 8.  Each entry provides the rgba
// values for the liquid fill gradient (surface + deep) and outer glow shadow.
const GLASS_PALETTE = [
  //  0 — Red (crimson)
  { liquidColor: 'rgba(180, 15, 15, 0.78)', liquidColorDeep: 'rgba(120, 8, 8, 0.88)', borderColor: 'rgba(255, 200, 200, 0.45)', outerGlowColor: 'rgba(200, 30, 30, 0.32)' },
  //  1 — Red → Orange ¼
  { liquidColor: 'rgba(185, 31, 13, 0.78)', liquidColorDeep: 'rgba(125, 19, 7, 0.88)', borderColor: 'rgba(255, 203, 190, 0.45)', outerGlowColor: 'rgba(203, 45, 26, 0.32)' },
  //  2 — Red → Orange ½
  { liquidColor: 'rgba(190, 48, 10, 0.78)', liquidColorDeep: 'rgba(130, 29, 7, 0.88)', borderColor: 'rgba(255, 205, 180, 0.45)', outerGlowColor: 'rgba(205, 60, 23, 0.31)' },
  //  3 — Red → Orange ¾
  { liquidColor: 'rgba(195, 64, 8, 0.78)', liquidColorDeep: 'rgba(135, 40, 6, 0.88)', borderColor: 'rgba(255, 208, 170, 0.45)', outerGlowColor: 'rgba(208, 75, 19, 0.31)' },
  //  4 — Orange (amber)
  { liquidColor: 'rgba(200, 80, 5, 0.78)', liquidColorDeep: 'rgba(140, 50, 5, 0.88)', borderColor: 'rgba(255, 210, 160, 0.45)', outerGlowColor: 'rgba(210, 90, 15, 0.30)' },
  //  5 — Orange → Yellow ¼
  { liquidColor: 'rgba(198, 103, 4, 0.77)', liquidColorDeep: 'rgba(138, 66, 4, 0.87)', borderColor: 'rgba(255, 219, 155, 0.45)', outerGlowColor: 'rgba(206, 111, 11, 0.30)' },
  //  6 — Orange → Yellow ½
  { liquidColor: 'rgba(195, 125, 3, 0.77)', liquidColorDeep: 'rgba(135, 83, 3, 0.87)', borderColor: 'rgba(255, 228, 150, 0.45)', outerGlowColor: 'rgba(203, 133, 8, 0.29)' },
  //  7 — Orange → Yellow ¾
  { liquidColor: 'rgba(193, 148, 1, 0.76)', liquidColorDeep: 'rgba(133, 99, 1, 0.86)', borderColor: 'rgba(255, 236, 145, 0.45)', outerGlowColor: 'rgba(199, 154, 4, 0.29)' },
  //  8 — Yellow (citrine)
  { liquidColor: 'rgba(190, 170, 0, 0.75)', liquidColorDeep: 'rgba(130, 115, 0, 0.85)', borderColor: 'rgba(255, 245, 140, 0.45)', outerGlowColor: 'rgba(195, 175, 0, 0.28)' },
  //  9 — Yellow → Green ¼
  { liquidColor: 'rgba(145, 160, 8, 0.76)', liquidColorDeep: 'rgba(99, 108, 5, 0.86)', borderColor: 'rgba(231, 244, 150, 0.45)', outerGlowColor: 'rgba(150, 168, 10, 0.29)' },
  // 10 — Yellow → Green ½
  { liquidColor: 'rgba(100, 150, 15, 0.77)', liquidColorDeep: 'rgba(68, 100, 9, 0.87)', borderColor: 'rgba(208, 243, 160, 0.45)', outerGlowColor: 'rgba(105, 160, 19, 0.29)' },
  // 11 — Yellow → Green ¾
  { liquidColor: 'rgba(55, 140, 23, 0.77)', liquidColorDeep: 'rgba(36, 93, 14, 0.87)', borderColor: 'rgba(184, 241, 170, 0.45)', outerGlowColor: 'rgba(60, 153, 29, 0.30)' },
  // 12 — Green (emerald)
  { liquidColor: 'rgba(10, 130, 30, 0.78)', liquidColorDeep: 'rgba(5, 85, 18, 0.88)', borderColor: 'rgba(160, 240, 180, 0.45)', outerGlowColor: 'rgba(15, 145, 38, 0.30)' },
  // 13 — Green → Blue ¼
  { liquidColor: 'rgba(13, 113, 73, 0.79)', liquidColorDeep: 'rgba(7, 73, 48, 0.88)', borderColor: 'rgba(160, 230, 199, 0.45)', outerGlowColor: 'rgba(18, 128, 79, 0.31)' },
  // 14 — Green → Blue ½
  { liquidColor: 'rgba(15, 95, 115, 0.79)', liquidColorDeep: 'rgba(9, 60, 78, 0.88)', borderColor: 'rgba(160, 220, 218, 0.45)', outerGlowColor: 'rgba(20, 110, 119, 0.31)' },
  // 15 — Green → Blue ¾
  { liquidColor: 'rgba(18, 78, 158, 0.80)', liquidColorDeep: 'rgba(10, 48, 108, 0.88)', borderColor: 'rgba(160, 210, 236, 0.45)', outerGlowColor: 'rgba(23, 93, 160, 0.32)' },
  // 16 — Blue (sapphire)
  { liquidColor: 'rgba(20, 60, 200, 0.80)', liquidColorDeep: 'rgba(12, 35, 138, 0.88)', borderColor: 'rgba(160, 200, 255, 0.45)', outerGlowColor: 'rgba(25, 75, 200, 0.32)' },
  // 17 — Blue → Cyan ¼
  { liquidColor: 'rgba(15, 81, 194, 0.80)', liquidColorDeep: 'rgba(9, 50, 133, 0.88)', borderColor: 'rgba(155, 209, 254, 0.45)', outerGlowColor: 'rgba(19, 96, 198, 0.32)' },
  // 18 — Blue → Cyan ½
  { liquidColor: 'rgba(10, 103, 188, 0.79)', liquidColorDeep: 'rgba(6, 65, 128, 0.88)', borderColor: 'rgba(150, 218, 253, 0.45)', outerGlowColor: 'rgba(13, 118, 195, 0.31)' },
  // 19 — Blue → Cyan ¾
  { liquidColor: 'rgba(5, 124, 181, 0.79)', liquidColorDeep: 'rgba(3, 80, 123, 0.88)', borderColor: 'rgba(145, 226, 251, 0.45)', outerGlowColor: 'rgba(6, 139, 193, 0.31)' },
  // 20 — Cyan (aquamarine)
  { liquidColor: 'rgba(0, 145, 175, 0.78)', liquidColorDeep: 'rgba(0, 95, 118, 0.88)', borderColor: 'rgba(140, 235, 250, 0.45)', outerGlowColor: 'rgba(0, 160, 190, 0.30)' },
  // 21 — Cyan → Magenta ¼
  { liquidColor: 'rgba(49, 109, 166, 0.78)', liquidColorDeep: 'rgba(34, 71, 112, 0.88)', borderColor: 'rgba(169, 216, 245, 0.45)', outerGlowColor: 'rgba(50, 125, 180, 0.31)' },
  // 22 — Cyan → Magenta ½
  { liquidColor: 'rgba(98, 73, 158, 0.78)', liquidColorDeep: 'rgba(68, 48, 107, 0.88)', borderColor: 'rgba(198, 198, 240, 0.45)', outerGlowColor: 'rgba(100, 90, 170, 0.31)' },
  // 23 — Cyan → Magenta ¾
  { liquidColor: 'rgba(146, 36, 149, 0.78)', liquidColorDeep: 'rgba(101, 24, 102, 0.88)', borderColor: 'rgba(226, 179, 235, 0.45)', outerGlowColor: 'rgba(150, 55, 160, 0.32)' },
  // 24 — Magenta
  { liquidColor: 'rgba(195, 0, 140, 0.78)', liquidColorDeep: 'rgba(135, 0, 95, 0.88)', borderColor: 'rgba(255, 160, 230, 0.45)', outerGlowColor: 'rgba(200, 20, 150, 0.32)' },
  // 25 — Magenta → Indigo ¼
  { liquidColor: 'rgba(163, 0, 155, 0.79)', liquidColorDeep: 'rgba(112, 0, 106, 0.88)', borderColor: 'rgba(241, 163, 236, 0.45)', outerGlowColor: 'rgba(170, 20, 164, 0.32)' },
  // 26 — Magenta → Indigo ½
  { liquidColor: 'rgba(130, 0, 170, 0.79)', liquidColorDeep: 'rgba(89, 0, 117, 0.88)', borderColor: 'rgba(228, 165, 243, 0.45)', outerGlowColor: 'rgba(140, 20, 178, 0.32)' },
  // 27 — Magenta → Indigo ¾
  { liquidColor: 'rgba(98, 0, 185, 0.80)', liquidColorDeep: 'rgba(65, 0, 127, 0.88)', borderColor: 'rgba(214, 168, 249, 0.45)', outerGlowColor: 'rgba(110, 20, 191, 0.32)' },
  // 28 — Indigo
  { liquidColor: 'rgba(65, 0, 200, 0.80)', liquidColorDeep: 'rgba(42, 0, 138, 0.88)', borderColor: 'rgba(200, 170, 255, 0.45)', outerGlowColor: 'rgba(80, 20, 205, 0.32)' },
  // 29 — Indigo → Red ¼
  { liquidColor: 'rgba(94, 4, 154, 0.80)', liquidColorDeep: 'rgba(62, 2, 106, 0.88)', borderColor: 'rgba(214, 178, 241, 0.45)', outerGlowColor: 'rgba(110, 23, 161, 0.32)' },
  // 30 — Indigo → Red ½
  { liquidColor: 'rgba(123, 8, 108, 0.79)', liquidColorDeep: 'rgba(81, 4, 73, 0.88)', borderColor: 'rgba(228, 185, 228, 0.45)', outerGlowColor: 'rgba(140, 25, 118, 0.32)' },
  // 31 — Indigo → Red ¾
  { liquidColor: 'rgba(151, 11, 61, 0.79)', liquidColorDeep: 'rgba(101, 6, 41, 0.88)', borderColor: 'rgba(241, 193, 214, 0.45)', outerGlowColor: 'rgba(170, 28, 74, 0.32)' },
];

/**
 * Creates a procedurally generated glass-vessel surface for vault potion cards.
 *
 * Colour is selected by `seed % 32` from GLASS_PALETTE
 * (32 entries: red, orange, yellow, green, blue, cyan, magenta, indigo,
 * plus 3 interpolated steps between each adjacent pair, wrapping back to red).
 *
 * The surface renders a clear glass container filled with coloured liquid.
 * Fill level is deterministic per seed: the air zone ranges from 5 % (nearly
 * full) to 55 % (about half empty), derived via `seed * 41 + 23` so fill level
 * does not correlate with colour selection (`seed % 32`).
 *
 * A CSS hard-stop gradient at `airPercent` from the top divides the card into:
 *  - Top airPercent %   — empty air zone: transparent, glass wall highlights only
 *  - Remaining %        — liquid zone: opaque coloured liquid, darkening toward base
 *
 * Additional layers add the meniscus glint at the liquid surface, glass wall
 * specular stripes (left dominant + right catch-light), top rim glint, and an
 * internal refracted-light caustic positioned inside the liquid zone.
 *
 * @param seed - Controls colour selection and fill level (0–99999)
 */
export function createColouredGlassSurface(seed: number) {
  const _hit = _frostedGlassSurfaceCache.get(seed);
  if (_hit !== undefined) return _hit;

  const color = GLASS_PALETTE[seed % GLASS_PALETTE.length];
  // Fill level: 5–55 % air zone, derived independently of colour (seed % 32)
  const airPercent = 5 + ((seed * 41 + 23) % 51);

  const _result = {
    position: 'relative' as const,
    overflow: 'visible' as const,
    backgroundColor: 'transparent',
    backdropFilter: 'blur(10px) saturate(140%)',
    WebkitBackdropFilter: 'blur(10px) saturate(140%)',
    backgroundImage: [
      // 1. Left glass wall specular — dominant vertical highlight (full height)
      'linear-gradient(to right, rgba(255,255,255,0.58) 0%, rgba(255,255,255,0.25) 8%, rgba(255,255,255,0.05) 22%, rgba(255,255,255,0) 38%)',
      // 2. Right glass wall catch-light — secondary narrower highlight (full height)
      'linear-gradient(to left, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.05) 8%, rgba(255,255,255,0) 20%)',
      // 3. Top rim glint — bright at the glass opening
      'linear-gradient(to bottom, rgba(255,255,255,0.48) 0%, rgba(255,255,255,0) 8%)',
      // 4. Meniscus highlight — thin bright band tracking the liquid surface
      `linear-gradient(to bottom, rgba(255,255,255,0) ${airPercent - 2}%, rgba(255,255,255,0.38) ${airPercent - 0.5}%, rgba(255,255,255,0) ${airPercent + 2}%)`,
      // 5. Liquid fill — hard stop at airPercent: air above (transparent), liquid below
      `linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) ${airPercent}%, ${color.liquidColor} ${airPercent}%, ${color.liquidColorDeep} 100%)`,
      // 6. Internal light caustic — stays inside the liquid zone (≥20 pp below surface)
      `radial-gradient(ellipse 38% 18% at 60% ${Math.min(airPercent + 20, 88)}%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 100%)`,
    ].join(', '),
    backgroundSize: 'cover, cover, cover, cover, cover, cover',
    // border: `1px solid ${color.borderColor}`,
    borderRadius: '12px',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.55)',
      'inset 0 -2px 4px rgba(0,0,0,0.25)',
      `0 6px 24px ${color.outerGlowColor}`,
      '0 1px 6px rgba(0,0,0,0.18)',
    ].join(', '),
    // ── Bottle neck — narrow glass tube above the card body ─────────────────
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: 'calc(100% - 2px)',
      width: '28%',
      height: '32px',
      backgroundColor: 'transparent',
      backdropFilter: 'blur(10px) saturate(140%)',
      WebkitBackdropFilter: 'blur(10px) saturate(140%)',
      backgroundImage: [
        // Left glass wall specular (wider % since the neck is narrow)
        'linear-gradient(to right, rgba(255,255,255,0.58) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0) 60%)',
        // Right glass wall catch-light
        'linear-gradient(to left, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.06) 20%, rgba(255,255,255,0) 40%)',
        // Coloured light bleeding up into the neck from the liquid below
        `linear-gradient(to top, ${color.outerGlowColor} 0%, rgba(0,0,0,0) 70%)`,
      ].join(', '),
      borderTop: `1px solid ${color.borderColor}`,
      borderLeft: `1px solid ${color.borderColor}`,
      borderRight: `1px solid ${color.borderColor}`,
      borderBottom: 'none',
      borderRadius: '4px 4px 0 0',
      boxShadow: [
        'inset 0 1px 0 rgba(255,255,255,0.45)',
        `0 -4px 12px ${color.outerGlowColor}`,
      ].join(', '),
      zIndex: 1,
      pointerEvents: 'none' as const,
    },
    // ── Cork stopper — seals the top of the neck ─────────────────────────────
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: 'calc(100% + 30px)',
      width: '32%',
      height: '14px',
      backgroundColor: 'rgba(170, 110, 50, 0.95)',
      backgroundImage: [
        // Cylindrical 3D shading — darker at the side edges, lighter at centre
        'linear-gradient(to right, rgba(100,60,20,0.70) 0%, rgba(215,155,75,0.20) 22%, rgba(240,185,100,0.08) 50%, rgba(215,155,75,0.20) 78%, rgba(100,60,20,0.70) 100%)',
        // Top-face highlight — light catching the flat upper surface
        'linear-gradient(to bottom, rgba(245,210,155,0.55) 0%, rgba(0,0,0,0) 40%)',
        // Bottom shadow — where the cork sits inside the neck opening
        'linear-gradient(to top, rgba(70,40,15,0.45) 0%, rgba(0,0,0,0) 50%)',
      ].join(', '),
      border: '1px solid rgba(120, 75, 30, 0.75)',
      borderRadius: '3px 3px 1px 1px',
      boxShadow: [
        'inset 0 1px 0 rgba(245,210,155,0.50)',
        'inset 0 -1px 0 rgba(60,35,10,0.45)',
        '0 2px 6px rgba(0,0,0,0.45)',
        `0 -3px 10px ${color.outerGlowColor}`,
      ].join(', '),
      zIndex: 2,
      pointerEvents: 'none' as const,
    },
  };
  _frostedGlassSurfaceCache.set(seed, _result);
  return _result;
}

/**
 * Creates a procedurally generated ice surface style object for use in
 * MUI sx props and theme styleOverrides. Each seed produces a unique
 * crystallographic structure with cracks and texture imperfections.
 *
 * Two layout modes:
 *
 *  **Default** (`card: false`) — ice properties are applied directly to
 *  the element. Works for Paper, AppBar, Drawer, etc. where no
 *  content/icicles need to overflow the mask boundary.
 *
 *  **Card mode** (`card: true`) — ice surface, border, shadow, and mask
 *  are rendered in a `::before` pseudo-element (like `createParchmentSurface`).
 *  The element itself is transparent with `overflow: visible`, so icicles
 *  (`::after`) and box-shadows are not clipped by the mask. A gloss overlay
 *  is baked into the `::before` background stack.
 *
 * @param seed - Controls the unique crystal arrangement (0–99999)
 */
export function createIceSurface(
  seed: number,
  options?: { transparent?: boolean; icicles?: boolean; card?: boolean },
) {
  const t = options?.transparent ?? false;
  const icicles = options?.icicles ?? false;
  const card = options?.card ?? false;
  const _cacheKey = `${seed}:${t ? 1 : 0}:${icicles ? 1 : 0}:${card ? 1 : 0}`;
  const _hit = _iceSurfaceCache.get(_cacheKey);
  if (_hit !== undefined) return _hit;
  const svgUrl = generateIceSVG(seed);
  const icicleSeed = (seed * 13 + 43) % 99999;

  // ── Shared surface properties ───────────────────────────────────────────
  const bgColor = t ? 'rgba(200, 225, 248, 0.30)' : 'rgba(200, 225, 248, 0.68)';
  const bgImage = [
    // Rainbow layers (if enabled) sit on top of the ice stack
    ...([]),
    ...([]),
    // Directional specular highlight
    t
      ? 'linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(210,235,255,0.05) 45%, rgba(185,215,245,0.03) 100%)'
      : 'linear-gradient(160deg, rgba(255,255,255,0.22) 0%, rgba(210,235,255,0.10) 45%, rgba(185,215,245,0.06) 100%)',
    // Radial caustic hotspot near top-left
    t
      ? 'radial-gradient(ellipse 60% 40% at 20% 15%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 100%)'
      : 'radial-gradient(ellipse 60% 40% at 20% 15%, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0) 100%)',
    // Procedural ice crystal + crack texture
    svgUrl,
  ];
  const rainbowExtraSizes = [
    ...([]),
    ...([]),
  ];
  const bgSize = [...rainbowExtraSizes, 'cover', 'cover', '512px 512px'];
  const borderVal = (t ? '1px solid rgba(230, 245, 255, 0.40)' : '1px solid rgba(230, 245, 255, 0.92)');
  const shadowVal = (t
    ? '0 8px 20px rgba(5, 20, 38, 0.12), ' +
    'inset 0 1px 0 rgba(240,250,255,0.25), ' +
    'inset 0 0 20px rgba(180,215,245,0.08)'
    : '0 12px 32px rgba(5, 20, 38, 0.32), ' +
    'inset 0 1px 0 rgba(240,250,255,0.55), ' +
    'inset 0 0 30px rgba(180,215,245,0.15)');

  // ── Icicle ::after (shared between both modes) ──────────────────────────
  const icicleAfter = icicles ? {
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      left: 0,
      right: 0,
      top: '100%',
      height: '36px',
      backgroundImage: generateIciclesSVG(icicleSeed),
      backgroundRepeat: 'repeat-x',
      backgroundSize: '400px 36px',
      backgroundPosition: 'left top',
      pointerEvents: 'none' as const,
      zIndex: 4,
      // Fade the very bottom so icicles don't end abruptly
      WebkitMaskImage: 'linear-gradient(to bottom, white 50%, transparent 100%)',
      maskImage: 'linear-gradient(to bottom, white 50%, transparent 100%)',
    },
  } : {};

  // ═══════════════════════════════════════════════════════════════════════
  //  Card mode — surface lives in ::before, root is transparent.
  //  The mask only clips the pseudo-element so content, icicles,
  //  and outer box-shadows are never masked.
  // ═══════════════════════════════════════════════════════════════════════
  if (card) {
    // Merge gloss into the background stack (opacity pre-multiplied)
    // Rainbow mode tints the gloss with a faint prismatic highlight
    const glossGradient = (t
      ? 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 38%, rgba(255,255,255,0) 70%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.19) 0%, rgba(255,255,255,0.04) 38%, rgba(255,255,255,0) 70%)');

    const _result = {
      position: 'relative' as const,
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      border: 'none',
      boxShadow: 'none',
      overflow: 'visible' as const,
      // Reset any inherited mask from MuiPaper theme
      WebkitMaskImage: 'none',
      maskImage: 'none',
      // Ice surface rendered in ::before with chipped edge mask
      '&::before': {
        content: '""',
        position: 'absolute' as const,
        // Extend past card bounds so chipped edges are visible
        inset: -8,
        zIndex: 0,
        borderRadius: 0,
        pointerEvents: 'none' as const,
        // Reset the theme's gloss (uses `background` shorthand)
        background: 'none' as const,
        opacity: 1,
        transition: 'none',
        // Ice surface
        backgroundColor: bgColor,
        backgroundImage: [glossGradient, ...bgImage].join(', '),
        backgroundSize: ['cover', ...bgSize].join(', '),
        backdropFilter: 'blur(6px) saturate(110%)',
        WebkitBackdropFilter: 'blur(6px) saturate(110%)',
        border: borderVal,
        boxShadow: shadowVal,
        // Chipped ice edges on the ::before only
        ...generateIceEdgeMask(seed),
      },
      // Card content stacks above the ::before
      '& > *': {
        position: 'relative' as const,
        zIndex: 1,
      },
      ...icicleAfter,
    };
    _iceSurfaceCache.set(_cacheKey, _result);
    return _result;
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  Default mode — properties on the element itself.
  //  Best for Paper, AppBar, Drawer, Popover, Dialog — elements without
  //  icicles or complex overflow needs.
  // ═══════════════════════════════════════════════════════════════════════
  const _result = {
    backgroundColor: bgColor,
    backgroundImage: bgImage.join(', '),
    backgroundSize: bgSize.join(', '),
    backdropFilter: 'blur(6px) saturate(110%)',
    WebkitBackdropFilter: 'blur(6px) saturate(110%)',
    border: borderVal,
    boxShadow: shadowVal,
    // Reduce the gloss overlay on nested/transparent cards
    ...(t ? { '&::before': { opacity: 0.2 } } : {}),
    // Chipped, cracked ice edges with melt fade (size-independent)
    ...generateIceEdgeMask(seed),
    ...icicleAfter,
  };
  _iceSurfaceCache.set(_cacheKey, _result);
  return _result;
}

export const recessedText = {
  color: '#0e345d',
  textShadow: '0 1px 0 rgba(255, 255, 255, 0.25), 0 -1px 1px rgba(7, 40, 70, 0.30)',
};

/**
 * Generates a procedural SVG parchment texture as a CSS data: URL.
 *
 * Two feTurbulence layers are blended together:
 *  - fractalNoise: broad crumple folds and wrinkles
 *  - fractalNoise: fine paper grain / fiber detail
 * A feColorMatrix maps the greyscale blend to a warm tan/sepia palette.
 *
 * @param seed - Controls the unique crumple pattern (0–99999)
 */
function generateParchmentSVG(seed: number): string {
  const grainSeed = (seed * 7 + 13) % 99999;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">` +
    `<filter id="f" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">` +
    // Broad crumple folds — low-frequency fractal noise
    `<feTurbulence type="fractalNoise" baseFrequency="0.012 0.018" numOctaves="5" seed="${seed}" result="crumple"/>` +
    // Fine paper grain / fiber detail — higher-frequency fractal noise
    `<feTurbulence type="fractalNoise" baseFrequency="0.045 0.065" numOctaves="3" seed="${grainSeed}" result="grain"/>` +
    // Multiply blend: grain darkens in the folds of the crumple base
    `<feBlend in="crumple" in2="grain" mode="multiply" result="blend"/>` +
    // Map greyscale noise to subtle warm paper tones
    // Matched to paper.jpg: light, uniform ~rgb(232,216,197), subtle texture
    `<feColorMatrix type="matrix" ` +
    `values="0.12 0.04 0 0 0.68  0.10 0.08 0 0 0.62  0.04 0.02 0.02 0 0.54  0 0 0 0.18 0" ` +
    `in="blend"/>` +
    `</filter>` +
    `<rect width="512" height="512" filter="url(#f)"/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Generates a procedural SVG simulating water bleeding through parchment,
 * as though the paper is resting on wet ice. Produces irregular, organic
 * blotches with soft feathered edges and a cool blue-grey tint.
 *
 * The filter chain:
 *  1. Low-frequency fractalNoise creates large organic blob shapes
 *  2. feComponentTransfer with a steep discrete table thresholds noise
 *     into distinct wet/dry regions (most of the surface stays dry)
 *  3. A second higher-frequency turbulence adds fine capillary detail
 *     at patch edges where water wicks along paper fibres
 *  4. feGaussianBlur softens the boundaries for a natural bleed look
 *  5. feColorMatrix tints patches a cool blue-grey — ice-melt water
 *     visible through translucent wet paper
 *
 * @param seed - Controls the unique bleed pattern (0–99999)
 */
function generateWaterBleedSVG(seed: number): string {
  const fibreSeed = (seed * 13 + 37) % 99999;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">` +
    `<filter id="f" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">` +
    // Large organic blob shapes — low-frequency fractal noise.
    // Asymmetric frequencies create elongated, naturalistic pools.
    `<feTurbulence type="fractalNoise" baseFrequency="0.007 0.012" numOctaves="5" seed="${seed}" result="noise"/>` +
    // Convert noise luminance → alpha channel for masking.
    // This gives us a smooth 0–1 alpha derived from the noise brightness.
    `<feColorMatrix type="luminanceToAlpha" in="noise" result="noiseA"/>` +
    // Threshold alpha into distinct wet/dry regions.
    // fractalNoise luminance clusters ~0.4–0.6.  This table ramp
    // keeps the lower half dry (alpha 0) and lets the brighter peaks
    // form visible wet patches (~25–35% coverage).
    // 9 values → breakpoints at 0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1.0
    `<feComponentTransfer in="noiseA" result="patches">` +
    `<feFuncA type="table" tableValues="0 0 0 0 0.2 0.65 1 1 1"/>` +
    `</feComponentTransfer>` +
    // Soft blur — water wicks gradually through paper fibres
    `<feGaussianBlur in="patches" stdDeviation="3.5" result="blurred"/>` +
    // Fine fibre texture to modulate wet patches with capillary detail
    `<feTurbulence type="fractalNoise" baseFrequency="0.04 0.06" numOctaves="3" seed="${fibreSeed}" result="fibres"/>` +
    `<feColorMatrix type="luminanceToAlpha" in="fibres" result="fibreA"/>` +
    // Scale fibre alpha to 0.55–1.0 so it adds texture without
    // obliterating the wet patches
    `<feComponentTransfer in="fibreA" result="fibreScaled">` +
    `<feFuncA type="linear" slope="0.45" intercept="0.55"/>` +
    `</feComponentTransfer>` +
    // Multiply fibre detail into the blurred patches:
    // output.a = blurred.a × fibreScaled.a
    `<feComposite in="blurred" in2="fibreScaled" operator="in" result="mask"/>` +
    // Flat water-stain colour — cool blue-grey suggesting ice-melt
    // visible through translucent wet paper
    `<feFlood flood-color="#8AACCC" flood-opacity="0.50" result="color"/>` +
    // Cut the colour to the wet-patch alpha mask
    `<feComposite in="color" in2="mask" operator="in"/>` +
    `</filter>` +
    `<rect width="512" height="512" filter="url(#f)"/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Generates CSS mask properties that produce rough, ragged parchment edges
 * with CONSISTENT edge thickness regardless of element size.
 *
 * Uses five mask layers: a solid centre fill plus four edge strips (top,
 * right, bottom, left) each rendered at a fixed CSS-pixel thickness.
 * Because the "thin" axis of each SVG edge strip always renders at the
 * same pixel size, the turbulence displacement is size-independent.
 *
 * @param seed - Controls the unique edge pattern (0–99999)
 * @returns CSS mask properties to spread into a style object
 */
function generateRaggedEdgeMask(seed: number): Record<string, string> {
  const _hit = _raggedEdgeMaskCache.get(seed);
  if (_hit !== undefined) return _hit;
  // Consistent rendered edge width in CSS pixels
  const EDGE_PX = 16;
  // SVG units: thin axis (edge depth) and long axis (along-edge length)
  const THIN = 40;
  const LONG = 2000;
  // Displacement scale in SVG units — rendered displacement ≈ DISP × EDGE_PX / THIN
  const DISP = 18;
  // Where the rect's ragged edge sits in the thin axis (SVG units)
  const INSET = 12;
  // Rect overshoot past SVG bounds on the three non-ragged sides
  const PAD = 20;

  const mkSvg = (
    w: number, h: number,
    rx: number, ry: number, rw: number, rh: number,
    edgeSeed: number,
    bfx: number, bfy: number,
  ) => {
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" preserveAspectRatio="none">` +
      `<filter id="f">` +
      `<feTurbulence type="turbulence" baseFrequency="${bfx} ${bfy}" numOctaves="4" seed="${edgeSeed}" result="n"/>` +
      `<feDisplacementMap in="SourceGraphic" in2="n" scale="${DISP}" xChannelSelector="R" yChannelSelector="G"/>` +
      `</filter>` +
      `<rect x="${rx}" y="${ry}" width="${rw}" height="${rh}" fill="white" filter="url(#f)"/>` +
      `</svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  };

  // Horizontal edges: low X freq (broad bumps along edge), moderate Y freq (detail in thin strip)
  const hBfx = 0.008;
  const hBfy = 0.06;
  // Vertical edges: swapped
  const vBfx = 0.06;
  const vBfy = 0.008;
  // Corners: moderate in both axes for coherent ragged edges on two sides
  const cBf = 0.04;

  // --- Edge strips (exclude corners — sized to inner area only) ---
  // Top: ragged on the bottom edge of the strip (rect overshoots top/left/right)
  const topUrl = mkSvg(LONG, THIN,
    -PAD, INSET, LONG + PAD * 2, THIN - INSET + PAD,
    seed, hBfx, hBfy);
  // Bottom: ragged on the top edge of the strip
  const bottomUrl = mkSvg(LONG, THIN,
    -PAD, -PAD, LONG + PAD * 2, THIN - INSET + PAD,
    (seed * 3 + 7) % 99999, hBfx, hBfy);
  // Left: ragged on the right edge of the strip
  const leftUrl = mkSvg(THIN, LONG,
    INSET, -PAD, THIN - INSET + PAD, LONG + PAD * 2,
    (seed * 5 + 11) % 99999, vBfx, vBfy);
  // Right: ragged on the left edge of the strip
  const rightUrl = mkSvg(THIN, LONG,
    -PAD, -PAD, THIN - INSET + PAD, LONG + PAD * 2,
    (seed * 7 + 13) % 99999, vBfx, vBfy);

  // --- Corner pieces — single displacement source produces coherent
  //     ragged edges on BOTH exposed sides, avoiding the artefact of
  //     two overlapping strips with different patterns. ---
  // Top-left: ragged on top + left (rect overshoots right + bottom)
  const tlUrl = mkSvg(THIN, THIN,
    INSET, INSET, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 11 + 17) % 99999, cBf, cBf);
  // Top-right: ragged on top + right (rect overshoots left + bottom)
  const trUrl = mkSvg(THIN, THIN,
    -PAD, INSET, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 13 + 19) % 99999, cBf, cBf);
  // Bottom-right: ragged on bottom + right (rect overshoots left + top)
  const brUrl = mkSvg(THIN, THIN,
    -PAD, -PAD, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 17 + 23) % 99999, cBf, cBf);
  // Bottom-left: ragged on bottom + left (rect overshoots right + top)
  const blUrl = mkSvg(THIN, THIN,
    INSET, -PAD, THIN - INSET + PAD, THIN - INSET + PAD,
    (seed * 19 + 29) % 99999, cBf, cBf);

  const E = `${EDGE_PX}px`;
  const inner = `calc(100% - ${EDGE_PX * 2}px)`;

  const images = [
    // Solid centre fill
    'linear-gradient(white, white)',
    // Edge strips (sans corners)
    topUrl, rightUrl, bottomUrl, leftUrl,
    // Corner pieces
    tlUrl, trUrl, brUrl, blUrl,
  ].join(', ');

  const sizes = [
    `${inner} ${inner}`,   // centre
    `${inner} ${E}`,       // top
    `${E} ${inner}`,       // right
    `${inner} ${E}`,       // bottom
    `${E} ${inner}`,       // left
    `${E} ${E}`,           // top-left
    `${E} ${E}`,           // top-right
    `${E} ${E}`,           // bottom-right
    `${E} ${E}`,           // bottom-left
  ].join(', ');

  const positions = [
    'center',
    'center top', 'right center', 'center bottom', 'left center',
    'left top', 'right top', 'right bottom', 'left bottom',
  ].join(', ');

  const _r: Record<string, string> = {
    WebkitMaskImage: images,
    maskImage: images,
    WebkitMaskSize: sizes,
    maskSize: sizes,
    WebkitMaskPosition: positions,
    maskPosition: positions,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
  };
  _raggedEdgeMaskCache.set(seed, _r);
  return _r;
}

/**
 * Generates a procedural SVG containing faint arcane sigils scattered
 * across the parchment. Uses a seeded PRNG to deterministically place
 * circles, arcs, runes, and geometric marks that look like hand-drawn
 * magical writing faded into the paper over time.
 *
 * @param seed - Controls sigil placement and variety (0–99999)
 */
function generateArcaneSigilsSVG(seed: number): string {
  // Simple seeded PRNG (mulberry32)
  const prng = (s: number) => {
    let t = s + 0x6d2b79f5;
    return () => {
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };
  const rand = prng(seed);

  const W = 512;
  const H = 512;

  // Helper: jitter a coordinate to simulate hand-drawn wobble
  const jit = (v: number, amount = 0.7) =>
    v + (rand() - 0.5) * amount * 2;

  // Helper: generate a wobbly circle as a path (hand-drawn feel)
  const wobbleCircle = (r: number, segments = 12) => {
    const pts: string[] = [];
    for (let j = 0; j <= segments; j++) {
      const a = (j / segments) * Math.PI * 2;
      const wobble = r + (rand() - 0.5) * r * 0.05;
      const x = Math.cos(a) * wobble;
      const y = Math.sin(a) * wobble;
      pts.push(j === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` :
        `L${jit(x).toFixed(1)},${jit(y).toFixed(1)}`);
    }
    return pts.join(' ') + 'Z';
  };

  // Helper: wobbly line between two points
  const wobbleLine = (x1: number, y1: number, x2: number, y2: number) => {
    const mx = (x1 + x2) / 2 + (rand() - 0.5) * 1.2;
    const my = (y1 + y2) / 2 + (rand() - 0.5) * 1.2;
    return `M${jit(x1).toFixed(1)},${jit(y1).toFixed(1)} ` +
      `Q${mx.toFixed(1)},${my.toFixed(1)} ${jit(x2).toFixed(1)},${jit(y2).toFixed(1)}`;
  };

  // Helper: wobbly polygon
  const wobblePoly = (points: [number, number][]) => {
    return points.map((p, j) =>
      j === 0 ? `M${jit(p[0]).toFixed(1)},${jit(p[1]).toFixed(1)}` :
        `L${jit(p[0]).toFixed(1)},${jit(p[1]).toFixed(1)}`
    ).join(' ') + 'Z';
  };

  // Icy-blue frost colours for sigil strokes
  const strokeColor = () => {
    const alpha = (0.30 + rand() * 0.18).toFixed(2);
    // Mix between pale ice-blue and white-frost
    const r = Math.round(170 + rand() * 60);
    const g = Math.round(200 + rand() * 45);
    const b = Math.round(235 + rand() * 20);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const dotColor = () => {
    const alpha = (0.30 + rand() * 0.15).toFixed(2);
    return `rgba(210,230,250,${alpha})`;
  };

  let paths = '';

  // Define SVG filters: jagged frost edge distortion + frost glow
  paths += `<defs>` +
    // Frost-edge filter: high-frequency turbulence displacement creates
    // jagged crystalline edges, then a soft glow is composited behind
    `<filter id="hw" x="-12%" y="-12%" width="124%" height="124%">` +
    // Low-freq warp for hand-drawn organic feel
    `<feTurbulence type="turbulence" baseFrequency="0.04" numOctaves="3" seed="${seed}" result="warp"/>` +
    `<feDisplacementMap in="SourceGraphic" in2="warp" scale="1.2" xChannelSelector="R" yChannelSelector="G" result="organic"/>` +
    // High-freq jagged frost crystallisation on edges
    `<feTurbulence type="turbulence" baseFrequency="0.12 0.14" numOctaves="4" seed="${(seed * 3 + 17) % 99999}" result="frost"/>` +
    `<feDisplacementMap in="organic" in2="frost" scale="2.8" xChannelSelector="R" yChannelSelector="G" result="jagged"/>` +
    // Frost glow: blur the jagged strokes, tint icy blue, composite behind
    `<feGaussianBlur in="jagged" stdDeviation="2.2" result="glow"/>` +
    `<feColorMatrix in="glow" type="matrix" values="0 0 0 0 0.78  0 0 0 0 0.90  0 0 0 0 1  0 0 0 0.7 0" result="tinted"/>` +
    `<feMerge><feMergeNode in="tinted"/><feMergeNode in="jagged"/></feMerge>` +
    `</filter>` +
    `</defs>`;

  // Generate 14–22 sigils for denser coverage
  const count = 14 + Math.floor(rand() * 9);
  for (let i = 0; i < count; i++) {
    const cx = 30 + rand() * (W - 60);
    const cy = 30 + rand() * (H - 60);
    const size = 14 + rand() * 32;
    const rot = Math.floor(rand() * 360);
    const type = Math.floor(rand() * 12);
    const sw = (1.8 + rand() * 1.2).toFixed(1);
    const sc = strokeColor();
    const dc = dotColor();

    const g = `<g transform="translate(${cx.toFixed(1)},${cy.toFixed(1)}) rotate(${rot})" ` +
      `stroke="${sc}" stroke-width="${sw}" fill="none" stroke-linecap="round" stroke-linejoin="round" filter="url(#hw)">`;

    if (type === 0) {
      // Concentric circles with cross
      paths += g +
        `<path d="${wobbleCircle(size * 0.9)}"/>` +
        `<path d="${wobbleCircle(size * 0.55)}"/>` +
        `<path d="${wobbleLine(-size, 0, size, 0)}"/>` +
        `<path d="${wobbleLine(0, -size, 0, size)}"/>` +
        `</g>`;
    } else if (type === 1) {
      // Triangle inscribed in circle
      const r = size * 0.85;
      const pts: [number, number][] = [0, 1, 2].map(j => {
        const a = (j * 120 - 90) * Math.PI / 180;
        return [Math.cos(a) * r, Math.sin(a) * r];
      });
      paths += g +
        `<path d="${wobbleCircle(r)}"/>` +
        `<path d="${wobblePoly(pts)}"/>` +
        `</g>`;
    } else if (type === 2) {
      // Star / pentagram
      const r = size * 0.85;
      const pts: [number, number][] = [];
      for (let j = 0; j < 5; j++) {
        const a = (j * 144 - 90) * Math.PI / 180;
        pts.push([Math.cos(a) * r, Math.sin(a) * r]);
      }
      paths += g +
        `<path d="${wobbleCircle(r)}"/>` +
        `<path d="${wobblePoly(pts)}"/>` +
        `</g>`;
    } else if (type === 3) {
      // Spiral arc with centre dot
      const r = size * 0.8;
      paths += g +
        `<path d="M${jit(0).toFixed(1)},${jit(0).toFixed(1)} ` +
        `A${(r * 0.3).toFixed(1)},${(r * 0.3).toFixed(1)} 0 0 1 ${jit(r * 0.5).toFixed(1)},${jit(r * 0.15).toFixed(1)} ` +
        `A${(r * 0.5).toFixed(1)},${(r * 0.5).toFixed(1)} 0 0 1 ${jit(-r * 0.1).toFixed(1)},${jit(r * 0.7).toFixed(1)} ` +
        `A${(r * 0.8).toFixed(1)},${(r * 0.8).toFixed(1)} 0 0 1 ${jit(-r * 0.8).toFixed(1)},${jit(-r * 0.3).toFixed(1)}"/>` +
        `<circle r="${(size * 0.10).toFixed(1)}" fill="${dc}"/>` +
        `</g>`;
    } else if (type === 4) {
      // Rune: vertical stave with angled bars and dot
      const h = size * 0.9;
      paths += g +
        `<path d="${wobbleLine(0, -h, 0, h)}"/>` +
        `<path d="${wobbleLine(-h * 0.4, -h * 0.5, h * 0.4, -h * 0.3)}"/>` +
        `<path d="${wobbleLine(-h * 0.35, h * 0.2, h * 0.35, h * 0.4)}"/>` +
        `<circle cx="${jit(h * 0.4).toFixed(1)}" cy="${jit(-h * 0.3).toFixed(1)}" r="2.5" fill="${dc}"/>` +
        `</g>`;
    } else if (type === 5) {
      // Diamond with inner X
      const d = size * 0.85;
      const pts: [number, number][] = [[0, -d], [d, 0], [0, d], [-d, 0]];
      paths += g +
        `<path d="${wobblePoly(pts)}"/>` +
        `<path d="${wobbleLine(-d * 0.5, -d * 0.5, d * 0.5, d * 0.5)}"/>` +
        `<path d="${wobbleLine(d * 0.5, -d * 0.5, -d * 0.5, d * 0.5)}"/>` +
        `</g>`;
    } else if (type === 6) {
      // Eye of power — almond shape with circle pupil
      const r = size * 0.8;
      paths += g +
        `<path d="M${jit(-r).toFixed(1)},0 ` +
        `Q${jit(0).toFixed(1)},${jit(-r * 0.6).toFixed(1)} ${jit(r).toFixed(1)},0 ` +
        `Q${jit(0).toFixed(1)},${jit(r * 0.6).toFixed(1)} ${jit(-r).toFixed(1)},0Z"/>` +
        `<path d="${wobbleCircle(r * 0.28, 8)}"/>` +
        `<circle r="${(r * 0.08).toFixed(1)}" fill="${dc}"/>` +
        `</g>`;
    } else if (type === 7) {
      // Crescent moon
      const r = size * 0.8;
      const ri = r * 0.65;
      const offset = r * 0.35;
      paths += g +
        `<path d="M0,${jit(-r).toFixed(1)} ` +
        `A${r.toFixed(1)},${r.toFixed(1)} 0 1 1 0,${jit(r).toFixed(1)} ` +
        `A${ri.toFixed(1)},${ri.toFixed(1)} 0 1 0 0,${jit(-r).toFixed(1)}Z" ` +
        `transform="translate(${jit(offset).toFixed(1)},0)"/>` +
        `</g>`;
    } else if (type === 8) {
      // Hexagram (Star of David) in circle
      const r = size * 0.85;
      const up: [number, number][] = [0, 1, 2].map(j => {
        const a = (j * 120 - 90) * Math.PI / 180;
        return [Math.cos(a) * r, Math.sin(a) * r];
      });
      const down: [number, number][] = [0, 1, 2].map(j => {
        const a = (j * 120 + 90) * Math.PI / 180;
        return [Math.cos(a) * r, Math.sin(a) * r];
      });
      paths += g +
        `<path d="${wobbleCircle(r * 1.05)}"/>` +
        `<path d="${wobblePoly(up)}"/>` +
        `<path d="${wobblePoly(down)}"/>` +
        `</g>`;
    } else if (type === 9) {
      // Ankh / looped cross
      const h = size * 0.9;
      const loopR = h * 0.28;
      paths += g +
        `<path d="${wobbleLine(0, -h * 0.1, 0, h)}"/>` +
        `<path d="${wobbleLine(-h * 0.35, h * 0.25, h * 0.35, h * 0.25)}"/>` +
        `<path d="M${jit(-loopR).toFixed(1)},${jit(-h * 0.1).toFixed(1)} ` +
        `A${loopR.toFixed(1)},${(loopR * 1.2).toFixed(1)} 0 1 1 ${jit(loopR).toFixed(1)},${jit(-h * 0.1).toFixed(1)}"/>` +
        `</g>`;
    } else if (type === 10) {
      // Triple interconnected circles (Borromean-style)
      const r = size * 0.45;
      const off = r * 0.55;
      paths += g +
        `<path d="${wobbleCircle(r, 10)}" transform="translate(0,${(-off).toFixed(1)})"/>` +
        `<path d="${wobbleCircle(r, 10)}" transform="translate(${(-off * 0.87).toFixed(1)},${(off * 0.5).toFixed(1)})"/>` +
        `<path d="${wobbleCircle(r, 10)}" transform="translate(${(off * 0.87).toFixed(1)},${(off * 0.5).toFixed(1)})"/>` +
        `</g>`;
    } else {
      // Arrow rune — vertical stave with arrowhead and tail notch
      const h = size * 0.9;
      paths += g +
        `<path d="${wobbleLine(0, -h, 0, h)}"/>` +
        `<path d="${wobbleLine(-h * 0.3, -h * 0.6, 0, -h)}"/>` +
        `<path d="${wobbleLine(h * 0.3, -h * 0.6, 0, -h)}"/>` +
        `<path d="${wobbleLine(-h * 0.25, h * 0.7, h * 0.25, h * 0.5)}"/>` +
        `</g>`;
    }
  }

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">` +
    paths +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Generates a nine-sliced hand-drawn border frame as CSS multi-background
 * properties. Returns backgroundImage / backgroundSize / backgroundPosition /
 * backgroundRepeat ready to spread into an absolutely-positioned overlay
 * element that matches the card bounds exactly (inset: 0).
 *
 * The frame line sits FRAME px from the element edge.  Each corner carries
 * a diamond flourish at the junction and short serif stubs at the arm ends.
 * Lines are gently displaced by feTurbulence for a hand-drawn look.
 */
function generateHandDrawnBorderSVG(seed: number): Record<string, string> {
  const _hit = _handDrawnBorderCache.get(seed);
  if (_hit !== undefined) return _hit;

  const FRAME = 6;        // frame line offset from element edge (SVG units = CSS px)
  const C = 30;           // corner piece size & edge strip thickness (CSS px / SVG units)
  const LONG = 2000;      // long axis of stretchable edge SVGs (SVG units)
  const DISP = 3;         // displacement scale for hand-drawn wobble
  const INK = 'rgba(100,65,20,0.85)';  // main bracket / outer diamond
  const INK2 = 'rgba(100,65,20,0.55)';  // inner accent bracket & curls
  const SW = '1.5';
  const SW2 = '1.0';
  const PARCHMENT = 'rgba(241,237,230,0.65)'; // inner diamond fill (knockout)
  const DR_O = 4.5;  // outer diamond half-radius
  const DR_I = 2.0;  // inner diamond half-radius

  const mkFilter = (s: number) =>
    `<filter id="hw" x="-15%" y="-15%" width="130%" height="130%">` +
    `<feTurbulence type="turbulence" baseFrequency="0.035" numOctaves="3" seed="${s}" result="n"/>` +
    `<feDisplacementMap in="SourceGraphic" in2="n" scale="${DISP}" xChannelSelector="R" yChannelSelector="G"/>` +
    `</filter>`;

  const enc = (svg: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")` as string;

  const diamond = (cx: number, cy: number, r: number) =>
    `${cx},${(cy - r).toFixed(1)} ${(cx + r).toFixed(1)},${cy} ${cx},${(cy + r).toFixed(1)} ${(cx - r).toFixed(1)},${cy}`;

  // mkCorner: jx/jy = frame-line junction, hDir/vDir = ±1 (arm directions into the card).
  //
  // Elaborated corner design:
  //   • Main bracket arms — overshoot SVG edge by 4px (clipped) for seamless joins
  //   • Inner accent bracket — offset 3px inward from junction, 13px long, lighter ink
  //   • Decorative quadratic-bezier curls at each inner bracket arm end
  //   • Outer filled diamond + inner parchment-tinted outlined diamond at junction
  const mkCorner = (s: number, jx: number, jy: number, hDir: number, vDir: number) => {
    // Main bracket arms: extend 4px past the SVG edge to avoid visible gaps at joins
    const hEnd = jx + hDir * (C - FRAME + 4);
    const vEnd = jy + vDir * (C - FRAME + 4);

    // Inner accent bracket
    const iJx = jx + hDir * 3;
    const iJy = jy + vDir * 3;
    const iHEnd = iJx + hDir * 13;
    const iVEnd = iJy + vDir * 13;

    // Curls at inner arm ends — hook that turns toward the card interior
    const hCurl = `M ${iHEnd},${iJy} Q ${iHEnd + hDir * 3},${iJy} ${iHEnd + hDir * 2},${iJy + vDir * 4}`;
    const vCurl = `M ${iJx},${iVEnd} Q ${iJx},${iVEnd + vDir * 3} ${iJx + hDir * 4},${iVEnd + vDir * 2}`;

    return enc(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${C}" height="${C}" viewBox="0 0 ${C} ${C}">` +
      `<defs>${mkFilter(s)}</defs>` +
      // All lines share the displacement filter for a hand-drawn wobble
      `<g fill="none" stroke-linecap="round" stroke-linejoin="round" filter="url(#hw)">` +
      // Main bracket arms
      `<line x1="${jx}" y1="${jy}" x2="${hEnd}" y2="${jy}" stroke="${INK}" stroke-width="${SW}"/>` +
      `<line x1="${jx}" y1="${jy}" x2="${jx}" y2="${vEnd}" stroke="${INK}" stroke-width="${SW}"/>` +
      // Inner accent bracket
      `<line x1="${iJx}" y1="${iJy}" x2="${iHEnd}" y2="${iJy}" stroke="${INK2}" stroke-width="${SW2}"/>` +
      `<line x1="${iJx}" y1="${iJy}" x2="${iJx}" y2="${iVEnd}" stroke="${INK2}" stroke-width="${SW2}"/>` +
      // Decorative curls at inner bracket arm ends
      `<path d="${hCurl}" stroke="${INK2}" stroke-width="${SW2}"/>` +
      `<path d="${vCurl}" stroke="${INK2}" stroke-width="${SW2}"/>` +
      `</g>` +
      // Diamonds rendered without the displacement filter so they remain crisp
      `<polygon points="${diamond(jx, jy, DR_O)}" fill="${INK}" stroke="none"/>` +
      `<polygon points="${diamond(jx, jy, DR_I)}" fill="${PARCHMENT}" stroke="${INK}" stroke-width="0.7"/>` +
      `</svg>`
    );
  };

  // Edge strips: lines start/end 5px outside the SVG viewport (clipped) so
  // they butt flush against the corner pieces without visible gaps.
  //
  // IMPORTANT: filters use filterUnits="userSpaceOnUse" with explicit SVG-unit
  // coordinates.  Percentage-based filter regions (the default objectBoundingBox
  // mode) collapse to zero for lines with no height/width, making the filter
  // produce no output — which is why percentage-region filters are avoided here.
  const mkHEdge = (s: number, lineY: number) =>
    enc(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${LONG}" height="${C}" viewBox="0 0 ${LONG} ${C}" preserveAspectRatio="none">` +
      `<defs>` +
      `<filter id="hw" filterUnits="userSpaceOnUse" x="-5" y="${lineY - 8}" width="${LONG + 10}" height="16">` +
      `<feTurbulence type="turbulence" baseFrequency="0.035" numOctaves="3" seed="${s}" result="n"/>` +
      `<feDisplacementMap in="SourceGraphic" in2="n" scale="${DISP}" xChannelSelector="R" yChannelSelector="G"/>` +
      `</filter>` +
      `</defs>` +
      `<line x1="-5" y1="${lineY}" x2="${LONG + 5}" y2="${lineY}" stroke="${INK}" stroke-width="${SW}" stroke-linecap="round" filter="url(#hw)"/>` +
      `</svg>`
    );

  const mkVEdge = (s: number, lineX: number) =>
    enc(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${C}" height="${LONG}" viewBox="0 0 ${C} ${LONG}" preserveAspectRatio="none">` +
      `<defs>` +
      `<filter id="hw" filterUnits="userSpaceOnUse" x="${lineX - 8}" y="-5" width="16" height="${LONG + 10}">` +
      `<feTurbulence type="turbulence" baseFrequency="0.035" numOctaves="3" seed="${s}" result="n"/>` +
      `<feDisplacementMap in="SourceGraphic" in2="n" scale="${DISP}" xChannelSelector="R" yChannelSelector="G"/>` +
      `</filter>` +
      `</defs>` +
      `<line x1="${lineX}" y1="-5" x2="${lineX}" y2="${LONG + 5}" stroke="${INK}" stroke-width="${SW}" stroke-linecap="round" filter="url(#hw)"/>` +
      `</svg>`
    );

  // Top-left: junction at (FRAME, FRAME), arms go right and down
  const tlUrl = mkCorner((seed * 37 + 61) % 99999, FRAME, FRAME, +1, +1);
  // Top-right: junction at (C-FRAME, FRAME), arms go left and down
  const trUrl = mkCorner((seed * 41 + 67) % 99999, C - FRAME, FRAME, -1, +1);
  // Bottom-right: junction at (C-FRAME, C-FRAME), arms go left and up
  const brUrl = mkCorner((seed * 43 + 71) % 99999, C - FRAME, C - FRAME, -1, -1);
  // Bottom-left: junction at (FRAME, C-FRAME), arms go right and up
  const blUrl = mkCorner((seed * 47 + 73) % 99999, FRAME, C - FRAME, +1, -1);

  const topUrl = mkHEdge((seed * 53 + 79) % 99999, FRAME);
  const bottomUrl = mkHEdge((seed * 59 + 83) % 99999, C - FRAME);
  const leftUrl = mkVEdge((seed * 61 + 89) % 99999, FRAME);
  const rightUrl = mkVEdge((seed * 67 + 97) % 99999, C - FRAME);

  const E = `${C}px`;
  const inner = `calc(100% - ${C * 2}px)`;

  const _r: Record<string, string> = {
    backgroundImage: [tlUrl, trUrl, brUrl, blUrl, topUrl, bottomUrl, leftUrl, rightUrl].join(', '),
    backgroundSize: [
      `${E} ${E}`, `${E} ${E}`, `${E} ${E}`, `${E} ${E}`,
      `${inner} ${E}`, `${inner} ${E}`,
      `${E} ${inner}`, `${E} ${inner}`,
    ].join(', '),
    backgroundPosition: [
      'left top', 'right top', 'right bottom', 'left bottom',
      'center top', 'center bottom',
      'left center', 'right center',
    ].join(', '),
    backgroundRepeat: 'no-repeat',
  };
  _handDrawnBorderCache.set(seed, _r);
  return _r;
}

/**
 * Creates a procedurally generated crumpled parchment surface style object
 * for use in MUI sx props. Each seed produces a unique crumple/wrinkle
 * pattern with warm, aged tones, subtly darkened edges, and rough ragged
 * edges via an SVG displacement filter.
 *
 * @param seed - Controls the unique crumple pattern (0–99999)
 */
export function createParchmentSurface(seed: number) {
  const _hit = _parchmentSurfaceCache.get(seed);
  if (_hit !== undefined) return _hit;
  const svgUrl = generateParchmentSVG(seed);
  const sigilSeed = (seed * 11 + 31) % 99999;
  const sigilUrl = generateArcaneSigilsSVG(sigilSeed);
  const bleedSeed = (seed * 23 + 41) % 99999;
  const bleedUrl = generateWaterBleedSVG(bleedSeed);
  const edgeSeed = (seed * 3 + 7) % 99999;
  const borderSeed = (seed * 29 + 53) % 99999;

  const raggedEdgeMask = generateRaggedEdgeMask(edgeSeed);

  const _result = {
    // Card itself is transparent — the ::before pseudo-element provides the
    // parchment background so the displacement filter only affects the
    // surface, not the text content.
    position: 'relative' as const,
    backgroundColor: 'transparent',
    // Override MUI Paper's elevation gradient & Card theme defaults
    backgroundImage: 'none',
    borderRadius: 0,
    overflow: 'visible',
    border: 'none',
    boxShadow: 'none',
    transition: 'none',
    // Reset ice surface's frosted-glass blur from MuiPaper/MuiCard theme
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    // Parchment surface lives in a pseudo-element behind the content.
    // Explicitly override MUI Card theme's ::before gloss overlay.
    '&::before': {
      content: '""',
      position: 'absolute',
      // Extend well beyond card bounds so the masked parchment fully
      // covers the interior — the displacement filter can push the SVG
      // rect edges inward, and the 1% SVG inset also shrinks coverage.
      inset: -8,
      zIndex: 0,
      // Reset the theme's gloss gradient (uses `background` shorthand)
      background: 'none',
      opacity: 1,
      borderRadius: 0,
      pointerEvents: 'none' as const,
      transition: 'none',
      // Parchment surface — matched to paper.jpg
      // Lighter, more uniform base ~rgb(232,216,197) for strong text contrast
      backgroundColor: 'rgba(241, 237, 230, 0.78)',
      backgroundImage: [
        // Gentle centre warmth
        'radial-gradient(ellipse 50% 42% at 48% 42%, rgba(245,235,218,0.25) 0%, transparent 100%)',
        // Water bleed — irregular wet patches where ice-melt soaks through
        // bleedUrl,
        // Faint arcane sigils — scattered magical markings faded into the paper
        sigilUrl,
        // Procedural crumpled parchment texture
        svgUrl,
      ].join(', '),
      backgroundSize: ['cover', 'cover', 'cover', 'cover', 'cover', 'cover', 'cover', '512px 512px', '512px 512px'].join(', '),
      border: '1px solid rgba(160, 135, 100, 0.50)',
      // Ragged parchment edges via SVG displacement mask (size-independent)
      ...raggedEdgeMask,
    },
    // Ensure card content stacks above the ::before pseudo-element
    '& > *': {
      position: 'relative' as const,
      zIndex: 2,
    },
    // Decorative hand-drawn border frame — nine-sliced corners with diamond
    // flourishes, laid over the parchment surface at z-index 1.
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none' as const,
      borderRadius: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      ...generateHandDrawnBorderSVG(borderSeed),
    },
    // Override ice-theme text styles — dark sepia text for parchment readability
    ...parchmentText,
    '& .MuiTypography-root, & .MuiInputBase-root, & .MuiFormLabel-root, & .MuiChip-label': {
      ...parchmentText,
    },
    '& .MuiTypography-colorTextSecondary': {
      color: '#5a3d1e',
    },
    '& .MuiIconButton-root': {
      color: '#4a3018',
    },
  };
  _parchmentSurfaceCache.set(seed, _result);
  return _result;
}

/**
 * Creates a parchment label surface for use as a text-content wrapper inside
 * another surface (e.g. a paper label stuck onto a frosted-glass potion card).
 *
 * Uses the same procedural parchment system as `createParchmentSurface` with
 * two differences:
 *  - Tighter ::before inset (-6 vs -8) suited to a label, not a full card.
 *  - A drop-shadow on ::before that gives the label physical depth, as if it
 *    has been pasted onto the glass beneath it.
 *
 * @param seed - Controls the unique crumple/torn pattern (0–99999)
 */
export function createParchmentLabelSurface(seed: number) {
  const _hit = _parchmentLabelSurfaceCache.get(seed);
  if (_hit !== undefined) return _hit;
  const svgUrl = generateParchmentSVG(seed);
  const sigilSeed = (seed * 11 + 31) % 99999;
  const bleedSeed = (seed * 23 + 41) % 99999;
  const bleedUrl = generateWaterBleedSVG(bleedSeed);
  const edgeSeed = (seed * 3 + 7) % 99999;

  const raggedEdgeMask = generateRaggedEdgeMask(edgeSeed);

  const _result = {
    position: 'relative' as const,
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    borderRadius: 0,
    overflow: 'visible',
    border: 'none',
    boxShadow: 'none',
    transition: 'none',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    // Breathing room inside the label so text doesn't sit on the ragged edge
    padding: '8px 10px',
    '&::before': {
      content: '""',
      position: 'absolute',
      // Slightly tighter than the full-card surface: the label needn't
      // extend as far beyond its bounds as an edge-to-edge card surface.
      inset: -6,
      zIndex: 0,
      background: 'none',
      opacity: 1,
      borderRadius: 0,
      pointerEvents: 'none' as const,
      transition: 'none',
      // Drop-shadow lifts the label off the glass surface beneath it
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))',
      backgroundColor: 'rgba(241, 237, 230, 0.88)',
      backgroundImage: [
        'radial-gradient(ellipse 50% 42% at 48% 42%, rgba(245,235,218,0.25) 0%, transparent 100%)',
        // bleedUrl,
        svgUrl,
      ].join(', '),
      backgroundSize: ['cover', 'cover', 'cover', '512px 512px'].join(', '),
      border: '1px solid rgba(160, 135, 100, 0.50)',
      ...raggedEdgeMask,
    },
    '& > *': {
      position: 'relative' as const,
      zIndex: 2,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none' as const,
      borderRadius: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    ...parchmentText,
    '& .MuiTypography-root, & .MuiInputBase-root, & .MuiFormLabel-root, & .MuiChip-label': {
      ...parchmentText,
    },
    '& .MuiTypography-colorTextSecondary': {
      color: '#5a3d1e',
    },
    '& .MuiIconButton-root': {
      color: '#4a3018',
    },
  };
  _parchmentLabelSurfaceCache.set(seed, _result);
  return _result;
}

export const parchmentText = {
  color: '#0e345d',
  textShadow: 'none',
  fontWeight: 600,
};

// SVG tile for the hand-drawn double underline.
// Two cubic-bezier paths with matched start/end y-values for seamless
// horizontal tiling.  Both lines return to their centre y at x=0 and x=100
// so the 100 px tile repeats without a visible seam.
// Colour #644114 @ 85% opacity = rgba(100,65,20,0.85), matching INK in
// generateHandDrawnBorderSVG.
const _parchmentUnderlineSvg =
  `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="8">` +
  `<path d="M0,2.5 C16,1 30,4 50,2.3 C68,0.8 84,4 100,2.5"` +
  ` stroke="#644114" stroke-opacity="0.85" stroke-width="1" fill="none" stroke-linecap="round"/>` +
  `<path d="M0,6 C13,7.3 28,4.5 48,6.2 C66,7.5 82,4.8 100,6"` +
  ` stroke="#644114" stroke-opacity="0.85" stroke-width="1" fill="none" stroke-linecap="round"/>` +
  `</svg>`;

const _parchmentUnderlineUrl = `url("data:image/svg+xml,${encodeURIComponent(_parchmentUnderlineSvg)}")` as const;

/**
 * MUI sx-prop object that applies a hand-drawn double underline in warm
 * ink-brown ink, matching the parchment border frame colour.
 *
 * Usage:
 *   <Typography sx={{ ...parchmentUnderlineSx }}>Heading</Typography>
 *
 * An equivalent CSS class `.parchment-underline` is defined in index.css
 * for use via className when sx props are unavailable.
 */
export const parchmentUnderlineSx = {
  backgroundImage: _parchmentUnderlineUrl,
  backgroundRepeat: 'repeat-x',
  backgroundPosition: 'left bottom',
  backgroundSize: '100px 8px',
  paddingBottom: '8px',
} as const;

/**
 * Generates a procedural SVG rust / cast-iron texture as a CSS data: URL.
 *
 * Three feTurbulence layers produce an almost-black iron surface with
 * sparse rust spots: fine iron grain, low-frequency rust blobs, and
 * high-frequency oxidation detail within those blobs.
 *
 * @param seed - Controls the unique corrosion pattern (0–99999)
 */
function generateCastIronSVG(seed: number): string {
  const pittingSeed = (seed * 11 + 37) % 99999;
  const rustSpotSeed = (seed * 3 + 59) % 99999;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">` +
    `<filter id="f" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">` +
    // Fine iron grain — near-invisible subtle surface texture
    `<feTurbulence type="fractalNoise" baseFrequency="0.035 0.050" numOctaves="4" seed="${seed}" result="grain"/>` +
    // Sparse rust spots — low-frequency blobs that isolate patches
    `<feTurbulence type="fractalNoise" baseFrequency="0.008 0.012" numOctaves="3" seed="${rustSpotSeed}" result="spots"/>` +
    // High-frequency oxidation detail within the rust patches
    `<feTurbulence type="turbulence" baseFrequency="0.065 0.085" numOctaves="3" seed="${pittingSeed}" result="flake"/>` +
    // Multiply spots × flake so rust detail only shows where spots are bright
    `<feBlend in="spots" in2="flake" mode="multiply" result="rust"/>` +
    // Map rust noise to warm orange-brown, very low overall opacity
    `<feColorMatrix type="matrix" ` +
    `values="0.35 0.10 0 0 0.0  0.12 0.06 0 0 0.0  0.02 0.01 0.01 0 0.0  0 0 0 0.40 0" ` +
    `result="rustColor" in="rust"/>` +
    // Map grain to near-black iron with barely visible variation
    `<feColorMatrix type="matrix" ` +
    `values="0.04 0.02 0 0 0.02  0.03 0.02 0 0 0.02  0.02 0.01 0.01 0 0.02  0 0 0 0.25 0" ` +
    `result="ironColor" in="grain"/>` +
    // Screen the rust spots over the iron base
    `<feBlend in="rustColor" in2="ironColor" mode="screen"/>` +
    `</filter>` +
    `<rect width="512" height="512" filter="url(#f)"/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Creates a rusted cast-iron surface style object for use in MUI sx props.
 * Almost black with sparse rust spots. Each seed produces a unique pattern.
 *
 * @param seed - Controls the unique corrosion pattern (0–99999)
 */
export function createCastIronSurface(seed: number) {
  const _hit = _castIronSurfaceCache.get(seed);
  if (_hit !== undefined) return _hit;
  const svgUrl = generateCastIronSVG(seed);
  const _result = {
    backgroundColor: 'rgba(12, 10, 9, 0.98)',
    backgroundImage: [
      // Very faint top-light sheen
      'linear-gradient(175deg, rgba(40,30,22,0.15) 0%, rgba(10,8,6,0.05) 50%, rgba(5,4,3,0.15) 100%)',
      // Procedural rust-spotted iron texture
      svgUrl,
    ].join(', '),
    backgroundSize: ['cover', '512px 512px'].join(', '),
    border: '1px solid rgba(50, 35, 25, 0.6)',
    boxShadow:
      '0 8px 24px rgba(0, 0, 0, 0.55), ' +
      'inset 0 1px 0 rgba(80,60,40,0.10), ' +
      'inset 0 -1px 0 rgba(0,0,0,0.4), ' +
      'inset 0 0 20px rgba(0,0,0,0.3)',
  };
  _castIronSurfaceCache.set(seed, _result);
  return _result;
}

export const castIronText = {
  color: '#e8d5c0',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  Liquid Glass — parameterised surface helper
 *
 *  Every "frosted / liquid glass" component in the UI shares the same
 *  structural pattern: backdrop blur, 135° specular gradient, semi-
 *  transparent background, inset highlight, and optional outer drop-shadow.
 *
 *  Call `liquidGlass()` with only the values that differ from the defaults
 *  to get a consistent SxProps-compatible object.
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface LiquidGlassOptions {
  /** Backdrop blur in px (default 12) */
  blur?: number;
  /** Backdrop saturate % (default 160) */
  saturate?: number;
  /** Semi-transparent fill colour (default 'rgba(214, 233, 251, 0.28)') */
  bgColor?: string;
  /** Diagonal specular peak opacity 0–1 (default 0.42) */
  gradientPeak?: number;
  /** Gradient midpoint % (default 52) */
  gradientMid?: number;
  /** Border colour (default 'rgba(255, 255, 255, 0.30)') */
  borderColor?: string;
  /** Lower border colour — set to `undefined` to use `borderColor` (default undefined) */
  borderBottomColor?: string;
  /** Box-shadow layers (default: top-edge specular + inner glow + outer drop) */
  boxShadowLayers?: string[];
}

export function liquidGlass(options: LiquidGlassOptions = {}) {
  const {
    blur = 12,
    saturate = 160,
    bgColor,
    gradientPeak = 0.42,
    gradientMid = 52,
    borderColor = 'rgba(255, 255, 255, 0.30)',
    borderBottomColor,
    boxShadowLayers = [
      'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
      'inset 0 0 18px -4px rgba(255, 255, 255, 0.18)',
      '0 4px 16px rgba(0, 0, 0, 0.18)',
    ],
  } = options;

  const filter = `blur(${blur}px) saturate(${saturate}%)`;

  return {
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
    ...(bgColor != null ? { backgroundColor: bgColor } : {}),
    backgroundImage:
      `linear-gradient(135deg, rgba(255,255,255,${gradientPeak}) 0%, rgba(255,255,255,0.08) ${gradientMid}%, rgba(255,255,255,0) 100%)`,
    border: `1px solid ${borderColor}`,
    ...(borderBottomColor != null ? { borderBottomColor } : {}),
    boxShadow: boxShadowLayers.join(', '),
  } as const;
}

/**
 * Pre-built liquid-glass preset for Card surfaces (Vault items,
 * Warband member cards, etc.) that override the default ice-texture theme.
 *
 * Provides a stronger 14 px blur, an ice-blue border, a `::before` gloss
 * overlay, and strips the ragged-edge mask inherited from the MuiCard theme.
 */
export const frostedGlassCardSx = {
  ...liquidGlass({
    blur: 14,
    bgColor: 'rgba(214, 233, 251, 0.28)',
    gradientPeak: 0.36,
    gradientMid: 48,
    borderColor: 'rgba(230, 245, 255, 0.45)',
    boxShadowLayers: [
      'inset 0 1px 0 rgba(255, 255, 255, 0.45)',
      'inset 0 0 16px rgba(180, 215, 245, 0.08)',
      '0 4px 14px rgba(5, 20, 38, 0.10)',
    ],
  }),
  backgroundSize: 'cover',
  '&::before': {
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.04) 42%, rgba(255,255,255,0) 75%)',
    opacity: 0.4,
  },
  WebkitMaskImage: 'none',
  maskImage: 'none',
} as const;

export const bottleGlassCardSx = {
  ...liquidGlass({
    blur: 14,
    bgColor: 'rgba(6, 86, 19, 0.59)',
    gradientPeak: 0.36,
    gradientMid: 48,
    borderColor: 'rgba(230, 245, 255, 0.45)',
    boxShadowLayers: [
      'inset 0 1px 0 rgba(255, 255, 255, 0.45)',
      'inset 0 0 16px rgba(180, 215, 245, 0.08)',
      '0 4px 14px rgba(5, 20, 38, 0.10)',
    ],
  }),
  backgroundSize: 'cover',
  '&::before': {
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.04) 42%, rgba(255,255,255,0) 75%)',
    opacity: 0.4,
  },
  WebkitMaskImage: 'none',
  maskImage: 'none',
  borderRadius: '20px',
} as const;


// ─────────────────────────────────────────────────────────────────────────────
//  Blood Spatter – seeded procedural SVG
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a procedural blood-spatter SVG as a CSS `url("data:…")` string.
 *
 * Uses a seeded PRNG (mulberry32) to place 2–4 impact pools with satellite
 * micro-droplets, 0–3 tapered drips with terminal bulbs, and high-velocity
 * cast-off scatter. feTurbulence displacement warps hard ellipse edges into
 * organic blob shapes; a soft blur filter blends micro-droplets.
 *
 * Each seed yields a unique but deterministic spatter pattern.
 *
 * @param seed – Controls the unique arrangement (0–99999)
 */
export function generateBloodSpatterSVG(seed: number): string {
  // Mulberry32 seeded PRNG
  const prng = (s: number) => {
    let t = s + 0x6d2b79f5;
    return () => {
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };
  const rand = prng(seed);

  // Helpers
  const r = (min: number, max: number) => min + rand() * (max - min);
  const ri = (min: number, max: number) => Math.floor(r(min, max + 1));
  const pick = <T,>(arr: T[]): T => arr[ri(0, arr.length - 1)];
  const f2 = (n: number) => n.toFixed(2);

  const W = 800;
  const H = 500;

  // Blood colour palette – dark crimsons with varying opacity
  const fills = [
    '#780303', '#880404', '#900404', '#9A0505', '#AA0505',
    '#6A0202', '#6E0303', '#820404', '#8A0404', '#8C0404',
  ];
  const strokeFills = ['#700303', '#6C0303', '#680303'];
  const highlightStroke = '#9A0505';

  // Vary the filter seeds per member so turbulence displacement differs too
  const filterSeed1 = ri(1, 999);
  const filterSeed2 = ri(1, 999);

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}">`;

  // ── Filters ──
  svg += `<defs>`;
  svg += `<filter id="splat" x="-40%" y="-40%" width="180%" height="180%">`;
  svg += `<feTurbulence type="fractalNoise" baseFrequency="${f2(0.03 + rand() * 0.015)} ${f2(0.04 + rand() * 0.015)}" numOctaves="4" seed="${filterSeed1}" result="noise"/>`;
  svg += `<feDisplacementMap in="SourceGraphic" in2="noise" scale="${ri(18, 28)}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>`;
  svg += `<feGaussianBlur stdDeviation="${f2(1.4 + rand() * 0.8)}"/>`;
  svg += `</filter>`;
  svg += `<filter id="drip" x="-30%" y="-10%" width="160%" height="120%">`;
  svg += `<feTurbulence type="fractalNoise" baseFrequency="${f2(0.05 + rand() * 0.04)} ${f2(0.014 + rand() * 0.008)}" numOctaves="3" seed="${filterSeed2}" result="noise"/>`;
  svg += `<feDisplacementMap in="SourceGraphic" in2="noise" scale="${ri(5, 10)}" xChannelSelector="R" yChannelSelector="G" result="displaced"/>`;
  svg += `<feGaussianBlur stdDeviation="${f2(0.9 + rand() * 0.6)}"/>`;
  svg += `</filter>`;
  svg += `<filter id="soft" x="-50%" y="-50%" width="200%" height="200%">`;
  svg += `<feGaussianBlur stdDeviation="${f2(1.0 + rand() * 0.8)}"/>`;
  svg += `</filter>`;
  svg += `</defs>`;

  // ── Impact pools (2–4) ──
  const poolCount = ri(2, 4);
  // Divide the canvas into poolCount zones so pools don't cluster
  const zoneW = W / poolCount;

  for (let p = 0; p < poolCount; p++) {
    const cx = zoneW * p + r(zoneW * 0.15, zoneW * 0.85);
    const cy = r(60, H - 80);
    const mainRx = r(40, 90);
    const mainRy = r(25, 55);
    const rot = r(-30, 30);

    // Main blob cluster (2–4 overlapping ellipses)
    svg += `<g filter="url(#splat)">`;
    const blobCount = ri(2, 4);
    for (let b = 0; b < blobCount; b++) {
      const bx = cx + r(-mainRx * 0.4, mainRx * 0.4);
      const by = cy + r(-mainRy * 0.4, mainRy * 0.4);
      const brx = mainRx * r(0.3, 1.0);
      const bry = mainRy * r(0.3, 1.0);
      const brot = rot + r(-20, 20);
      const opacity = r(0.50, 0.82);
      svg += `<ellipse cx="${f2(bx)}" cy="${f2(by)}" rx="${f2(brx)}" ry="${f2(bry)}" fill="${pick(fills)}" fill-opacity="${f2(opacity)}" transform="rotate(${f2(brot)} ${f2(bx)} ${f2(by)})"/>`;
    }
    svg += `</g>`;

    // Satellite micro-droplets around this pool (3–6)
    const dropCount = ri(3, 6);
    svg += `<g filter="url(#soft)">`;
    for (let d = 0; d < dropCount; d++) {
      const angle = rand() * Math.PI * 2;
      const dist = mainRx * r(0.8, 2.0);
      const dx = cx + Math.cos(angle) * dist;
      const dy = cy + Math.sin(angle) * dist * 0.6;
      if (dx < -20 || dx > W + 20 || dy < -20 || dy > H + 20) continue;
      const dr = r(2, 9);
      const dopacity = r(0.34, 0.66);
      if (rand() > 0.5) {
        svg += `<circle cx="${f2(dx)}" cy="${f2(dy)}" r="${f2(dr)}" fill="${pick(fills)}" fill-opacity="${f2(dopacity)}"/>`;
      } else {
        const erx = dr;
        const ery = dr * r(1.5, 3.0);
        const erot = r(-40, 40);
        svg += `<ellipse cx="${f2(dx)}" cy="${f2(dy)}" rx="${f2(erx)}" ry="${f2(ery)}" fill="${pick(fills)}" fill-opacity="${f2(dopacity)}" transform="rotate(${f2(erot)} ${f2(dx)} ${f2(dy)})"/>`;
      }
    }
    svg += `</g>`;

    // Tapered drip from this pool (50% chance)
    if (rand() > 0.5 && cy < H - 100) {
      const dripX = cx + r(-mainRx * 0.3, mainRx * 0.3);
      const dripStartY = cy + mainRy * r(0.5, 0.9);
      const dripLen = r(80, Math.min(220, H - dripStartY - 30));
      const dripEndY = dripStartY + dripLen;
      // Slightly wobbly cubic bezier
      const wobble1 = r(-6, 6);
      const wobble2 = r(-6, 6);
      const midY1 = dripStartY + dripLen * 0.33;
      const midY2 = dripStartY + dripLen * 0.66;

      svg += `<g filter="url(#drip)">`;
      // Main drip stroke
      svg += `<path d="M${f2(dripX)} ${f2(dripStartY)} C${f2(dripX + wobble1)} ${f2(midY1)} ${f2(dripX + wobble2)} ${f2(midY2)} ${f2(dripX + wobble1 * 0.5)} ${f2(dripEndY)}" stroke="${pick(strokeFills)}" stroke-opacity="${f2(r(0.48, 0.66))}" stroke-width="${ri(6, 11)}" fill="none" stroke-linecap="round"/>`;
      // Thin highlight
      svg += `<path d="M${f2(dripX + 1)} ${f2(dripStartY)} C${f2(dripX + 1 + wobble1)} ${f2(midY1)} ${f2(dripX + 1 + wobble2)} ${f2(midY2)} ${f2(dripX + 0.5 + wobble1 * 0.5)} ${f2(dripEndY)}" stroke="${highlightStroke}" stroke-opacity="${f2(r(0.18, 0.32))}" stroke-width="${ri(2, 4)}" fill="none" stroke-linecap="round"/>`;
      // Terminal bulb
      const bulbRx = r(7, 14);
      const bulbRy = r(10, 18);
      svg += `<ellipse cx="${f2(dripX + wobble1 * 0.5)}" cy="${f2(dripEndY + bulbRy * 0.3)}" rx="${f2(bulbRx)}" ry="${f2(bulbRy)}" fill="${pick(strokeFills)}" fill-opacity="${f2(r(0.42, 0.62))}"/>`;
      // Tiny inner bulb
      svg += `<ellipse cx="${f2(dripX + wobble1 * 0.5)}" cy="${f2(dripEndY + bulbRy * 0.5)}" rx="${f2(bulbRx * 0.55)}" ry="${f2(bulbRy * 0.4)}" fill="#500202" fill-opacity="${f2(r(0.30, 0.46))}"/>`;
      svg += `</g>`;
    }
  }

  // ── High-velocity cast-off scatter (4–8 small blobs across top region) ──
  const scatterCount = ri(4, 8);
  svg += `<g filter="url(#splat)">`;
  for (let s = 0; s < scatterCount; s++) {
    const sx = r(40, W - 40);
    const sy = r(30, H * 0.4);
    const sr = r(4, 16);
    const sopacity = r(0.42, 0.70);
    if (rand() > 0.4) {
      svg += `<circle cx="${f2(sx)}" cy="${f2(sy)}" r="${f2(sr)}" fill="${pick(fills)}" fill-opacity="${f2(sopacity)}"/>`;
    } else {
      const srx = sr;
      const sry = sr * r(1.3, 2.8);
      const srot = r(-35, 35);
      svg += `<ellipse cx="${f2(sx)}" cy="${f2(sy)}" rx="${f2(srx)}" ry="${f2(sry)}" fill="${pick(fills)}" fill-opacity="${f2(sopacity)}" transform="rotate(${f2(srot)} ${f2(sx)} ${f2(sy)})"/>`;
    }
  }
  svg += `</g>`;

  // ── Extra micro-scatter across full canvas (3–5 tiny dots) ──
  svg += `<g filter="url(#soft)">`;
  const microCount = ri(3, 5);
  for (let m = 0; m < microCount; m++) {
    const mx = r(20, W - 20);
    const my = r(20, H - 20);
    const mr = r(2, 5);
    svg += `<circle cx="${f2(mx)}" cy="${f2(my)}" r="${f2(mr)}" fill="${pick(fills)}" fill-opacity="${f2(r(0.30, 0.52))}"/>`;
  }
  svg += `</g>`;

  svg += `</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
